import { Chip, TableCell, TableRow } from '@mui/material';
import { FC, Fragment, Reducer, useCallback, useEffect, useReducer } from 'react';
// components
import { Alert } from 'components/common/Alert';
import TableLoader from 'components/common/TableLoader';
import NoDataFound from 'components/common/NoDataFound';
import TableContainer from 'components/common/TableContainer';
import TableComponent from 'components/common/TableComponent';
import TableSearchField from 'components/common/TableSearchField';
import ActionMenuDropdown from 'components/common/ActionMenuDropdown';
import TableTabsComponent from 'components/common/TableTabsComponent';
// constants, reducers, graphql, styles
import {
  TABS,
  TabEnum,
  ACTIVE_TEXT,
  HTTP_STATUS,
  INACTIVE_TEXT,
  LOADING_TABLE_ROWS,
  EDIT_SAND_COMPONENT_ROUTE,
  PRODUCT_SAND_COMPONENT_TABLE_HEADER,
} from 'constants/index';
import { textWhiteSpace } from 'styles/commonComponentStyle';
import {
  useFindAllSandComponentTableLazyQuery,
  SandComponentsPayload,
  useUpdateSandComponentMutation,
} from 'generated/graphql';
import { State, Action, ActionType, initialState, sandComponentReducer } from 'reducer/sandComponentReducer';
import { useDebounce } from 'hooks/useDebounce';

const SandComponentTable: FC = () => {
  const [state, dispatch] = useReducer<Reducer<State, Action>>(sandComponentReducer, initialState);
  const { page, rowsPerPage, search, count, data, activeTab } = state;
  const debounceValue = useDebounce(search);

  const [findAllSandComponent, { loading: fetchLoading, error }] = useFindAllSandComponentTableLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { findAllSandComponent } = data;
      const { response, data: sandComponent, pagination } = findAllSandComponent || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        const { page, totalCount } = pagination || {};
        dispatch({ type: ActionType.SET_PAGE, page: page || 1 });
        dispatch({ type: ActionType.SET_COUNT, count: totalCount || 0 });
        dispatch({
          type: ActionType.SET_DATA,
          data: sandComponent as SandComponentsPayload['data'],
        });
      } else {
        resetPage();
      }
    },

    onError: () => {
      resetPage();
    },
  });

  const [updateSandComponentStatus, { loading: updateLoading }] = useUpdateSandComponentMutation({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { updateSandComponent } = data;
      const { response } = updateSandComponent || {};
      const { message, status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        Alert.success(message || '');
        fetchAllSandComponent();
      } else {
        Alert.error(message ?? '');
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const resetPage = () => {
    dispatch({ type: ActionType.SET_PAGE, page: 1 });
    dispatch({ type: ActionType.SET_COUNT, count: 0 });
    dispatch({ type: ActionType.SET_DATA, data: [] });
  };

  const fetchAllSandComponent = useCallback(async () => {
    await findAllSandComponent({
      variables: {
        findAllSandComponentInput: {
          paginationOptions: {
            limit: rowsPerPage,
            page,
          },
          search: debounceValue,
          ...(activeTab !== TabEnum.All && { isActive: activeTab === TabEnum.Active }),
        },
      },
    });
  }, [findAllSandComponent, rowsPerPage, page, activeTab, debounceValue]);

  useEffect(() => {
    fetchAllSandComponent();
  }, [fetchAllSandComponent]);

  const tabHandler = (_: React.SyntheticEvent<Element, Event>, value: string) => {
    dispatch({ type: ActionType.SET_ACTIVE_TAB, activeTab: value as TabEnum });
  };

  const onActiveHandler = async (id: string, status: boolean) => {
    await updateSandComponentStatus({
      variables: {
        updateSandComponentInput: {
          id,
          isActive: !status,
        },
      },
    });
  };

  const loading = fetchLoading || updateLoading;

  const noData = Boolean((!fetchLoading && data?.length === 0) || error);

  return (
    <TableContainer>
      <TableTabsComponent activeTab={activeTab} tabsList={TABS} tabHandler={tabHandler} />

      <TableSearchField
        search={search}
        onClear={() => dispatch({ type: ActionType.SET_SEARCH, search: '' })}
        onChange={(val) => dispatch({ type: ActionType.SET_SEARCH, search: val })}
      />

      <TableComponent
        noData={noData}
        page={page - 1}
        count={count}
        rowsPerPage={rowsPerPage}
        tableHeader={PRODUCT_SAND_COMPONENT_TABLE_HEADER}
        setPage={(p: number) => dispatch({ type: ActionType.SET_PAGE, page: p + 1 })}
        setRowsPerPage={(r: number) => dispatch({ type: ActionType.SET_ROWS_PER_PAGE, rowsPerPage: r })}>
        {loading ? (
          <TableLoader columns={3} rows={LOADING_TABLE_ROWS} />
        ) : (
          <Fragment>
            {data?.map((cell) => {
              const { id, name, isActive } = cell || {};
              return (
                <TableRow key={id}>
                  <TableCell sx={textWhiteSpace}>{name ?? '--'}</TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={isActive ? ACTIVE_TEXT : INACTIVE_TEXT}
                      color={isActive ? 'success' : 'error'}
                    />
                  </TableCell>

                  <TableCell>
                    <ActionMenuDropdown
                      id={id ?? ''}
                      isActive={!!isActive}
                      editRoute={EDIT_SAND_COMPONENT_ROUTE}
                      onActiveHandler={onActiveHandler}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </Fragment>
        )}
      </TableComponent>

      <NoDataFound noData={noData} />
    </TableContainer>
  );
};

export default SandComponentTable;
